import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Paper,
  Avatar,
  useMediaQuery,
} from '@mui/material';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import {
  answerSurveyQuestion,
  getActiveSurvey,
  getSurveyResponses,
} from './../../services/survey_services';
import { useAccount } from '../../contexts/Account';
import eventBus, { eventBusValues } from '../../eventBus';
import DefaultCompany from './../../icons/building_round.svg';
import { extractUrl } from './../../utils/urlUtils';
import ShareYourIdeasDialog from './ShareYourIdeasDialog';
import { addActivityFromAnswerSurvey } from '../../services/utils/surveys';

const classes = {
  surveyButton: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '14px',
    width: { xxs: '100%', xs: '140px' },
    height: '36px',
    borderRadius: '10px',
    margin: '0px',
  },
};

export default function CurrentPriorities({
  company,
  seeOpportunity,
  openProfile,
}) {
  const [surveyAnswer, setSurveyAnswer] = useState(null);
  const [oldAnswerBody, setOldAnswerBody] = useState(null);
  const [singleQuestion, setSingleQuestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [shareYourIdeasDialogOpen, setShareYourIdeasDialogOpen] =
    useState(false);
  const { accountDetails, isFreeTrial, isFreemium, type } = useAccount();
  const isOwnProfile = company.id === accountDetails.id;
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  useEffect(async () => {
    if (company && company.id) {
      const { data } = await getActiveSurvey({
        COMPANY_ID: company.id,
      });
      const questions = data.getActiveSurvey;
      if (questions && questions.length > 0) {
        setSingleQuestion(questions[0]);
        const response = await getSurveyResponses({
          USER_ID: accountDetails.id,
          SURVEY_ID: questions[0].surveyId,
        });

        if (response && response.data && response.data.getSurveyResponses) {
          const surveyResponses = response.data.getSurveyResponses;
          if (surveyResponses && surveyResponses.length > 0) {
            setSurveyAnswer(surveyResponses[0].body);
            setOldAnswerBody(surveyResponses[0].body);
          }
        }
      } else {
        setSingleQuestion(null);
      }
    }
  }, [company]);

  useEffect(() => {
    eventBus.on(eventBusValues.surveyAnswered, (activity) => {
      if (activity?.survey?.response) {
        setSurveyAnswer(activity.survey.response);
        setOldAnswerBody(activity.survey.response);
      }
    });

    return () => {
      eventBus.remove(eventBusValues.surveyAnswered);
    };
  }, []);

  async function handleSurveyResponse(surveyResponse) {
    setLoading(true);
    if (singleQuestion && oldAnswerBody === surveyResponse) {
      // console.log('Same survey answer, skipping update');
      return;
    }
    await answerSurveyQuestion({
      BODY: surveyResponse,
      SURVEY_ID: singleQuestion.surveyId,
      SURVEY_QUESTION_ID: singleQuestion.id,
      USER_ACCOUNT_TYPE: type,
      USER_ID: accountDetails.id,
    });
    setLoading(false);
    setOldAnswerBody(surveyResponse);
    if (oldAnswerBody) {
      return; // If oldAnswerBody is set, we assume this is an update to an existing answer
    }
    addActivityFromAnswerSurvey({
      accountDetails,
      surveyAnswer: surveyResponse,
      questionBody: singleQuestion.body,
    });
  }

  function openProfile(company) {
    eventBus.dispatch(eventBusValues.triggerCompanyDrawerAction, {
      company,
    });
  }
  return (
    <Box sx={{ height: '100%' }}>
      <Paper
        elevation={1}
        sx={{
          height: '100%',
          py: isSM ? '22px' : '32px',
          px: '20px',
          borderRadius: '12px',
          width: '100%',
          boxShadow: 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            mb: 2,
          }}
        >
          <Avatar
            alt={`${company.companyName} Logo`}
            src={
              company &&
              company.image &&
              company.image.id &&
              !company.image.id.includes('undefined')
                ? `${process.env.REACT_APP_IMAGES_URL}${company.image.id}`
                : DefaultCompany
            }
            sx={{
              width: 32,
              height: 32,
              mt: '-4px',
            }}
            onClick={() =>
              isOwnProfile
                ? openProfile(accountDetails)
                : seeOpportunity(company.id)
            }
          />
          <Typography
            sx={{
              mb: 2,
              fontWeight: '600',
              fontSize: '20px',
              lineHeight: '130%',
              color: '#001030',
              ml: '20px',
            }}
          >
            Current Priorities
          </Typography>
        </Box>
        <Stack spacing={2}>
          {singleQuestion && (
            <Card
              variant="outlined"
              sx={{ borderRadius: 2, bgcolor: '#fafcff' }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: '600',
                      fontSize: 18,
                      lineHeight: '24px',
                      color: '#232B35',
                    }}
                  >
                    Survey
                  </Typography>
                  <Box
                    sx={{
                      width: '100%',
                      display: { xxs: 'none', xs: 'flex' },
                      flexDirection: { xxs: 'column', xs: 'row' },
                      justifyContent: 'right',
                      gap: '8px',
                      // mt: '0px',
                    }}
                  >
                    <Button
                      variant={
                        surveyAnswer === 'Yes' ? 'contained' : 'outlined'
                      }
                      color="primary"
                      onClick={() => {
                        setSurveyAnswer('Yes');
                        handleSurveyResponse('Yes');
                      }}
                      sx={classes.surveyButton}
                      disabled={loading}
                    >
                      Yes
                    </Button>
                    <Button
                      variant={surveyAnswer === 'No' ? 'contained' : 'outlined'}
                      color="primary"
                      onClick={() => {
                        setSurveyAnswer('No');
                        handleSurveyResponse('No');
                      }}
                      sx={classes.surveyButton}
                      disabled={loading}
                    >
                      No
                    </Button>
                  </Box>
                </Box>
                <Typography
                  sx={{
                    fontWeight: '400',
                    fontSize: 16,
                    lineHeight: '150%',
                    color: '#232B35',
                  }}
                >
                  {singleQuestion.body}
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    display: { xxs: 'flex', xs: 'none' },
                    flexDirection: { xxs: 'column', xs: 'row' },
                    justifyContent: 'right',
                    mt: '16px',
                  }}
                >
                  <Button
                    variant={surveyAnswer === 'Yes' ? 'contained' : 'outlined'}
                    color="primary"
                    onClick={() => {
                      setSurveyAnswer('Yes');
                      handleSurveyResponse('Yes');
                    }}
                    sx={classes.surveyButton}
                    disabled={loading}
                  >
                    Yes
                  </Button>
                  <Button
                    variant={surveyAnswer === 'No' ? 'contained' : 'outlined'}
                    color="primary"
                    onClick={() => {
                      setSurveyAnswer('No');
                      handleSurveyResponse('No');
                    }}
                    sx={classes.surveyButton}
                    disabled={loading}
                  >
                    No
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}
          <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: '#fafcff' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: '600',
                    fontSize: 18,
                    lineHeight: '24px',
                    color: '#232B35',
                  }}
                >
                  Current Priority
                </Typography>
                <Button
                  id="boardroom-share-your-ideas"
                  variant="contained"
                  color="primary"
                  startIcon={<LightbulbIcon />}
                  onClick={() => {
                    if (isFreeTrial && !isFreemium) {
                      eventBus.dispatch(
                        eventBusValues.triggerAdvisorUpgradeAction
                      );
                    } else {
                      setShareYourIdeasDialogOpen(true);
                    }
                  }}
                  sx={{
                    textTransform: 'none',
                    bgcolor: '#4FB5F9',
                    borderRadius: '8px',
                    boxShadow: 'none',
                    display: {
                      xxs: 'none',
                      xs: company.currentGoal ? 'flex' : 'none',
                    },
                    width: { xs: 181 },
                    mt: { xs: 0 },
                    height: 36,
                    fontSize: 14,
                    fontWeight: '600',
                    '&:hover': { bgcolor: '#4FB5F9' },
                  }}
                >
                  Share Your Ideas
                </Button>
              </Box>
              <Typography
                sx={{
                  fontWeight: '400',
                  fontSize: 16,
                  lineHeight: '150%',
                  color: '#232B35',
                }}
              >
                {company.currentGoal
                  ? extractUrl(company.currentGoal)
                  : 'No current goal'}
              </Typography>
              <Button
                id="boardroom-share-your-ideas"
                variant="contained"
                color="primary"
                startIcon={<LightbulbIcon />}
                onClick={() => {
                  if (isFreeTrial && !isFreemium) {
                    eventBus.dispatch(
                      eventBusValues.triggerAdvisorUpgradeAction
                    );
                  } else {
                    setShareYourIdeasDialogOpen(true);
                  }
                }}
                sx={{
                  textTransform: 'none',
                  bgcolor: '#4FB5F9',
                  borderRadius: '8px',
                  boxShadow: 'none',
                  display: {
                    xxs: company.currentGoal ? 'flex' : 'none',
                    xs: 'none',
                  },
                  width: '100%',
                  mt: 1,
                  height: 42,
                  fontSize: 14,
                  fontWeight: '600',
                  '&:hover': { bgcolor: '#4FB5F9' },
                }}
              >
                Share Your Ideas
              </Button>
            </CardContent>
          </Card>
        </Stack>
      </Paper>
      <ShareYourIdeasDialog
        open={shareYourIdeasDialogOpen}
        onClose={() => setShareYourIdeasDialogOpen(false)}
        company={company}
      />
    </Box>
  );
}
